import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Search, ChevronRight, CheckCircle, XCircle } from 'lucide-react';

export default function SuperAdminHotels() {
  const [hotels, setHotels] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newHotel, setNewHotel] = useState({ hotelName: '', adminName: '', adminEmail: '', adminPassword: '', plan: 'STARTER' });
  const navigate = useNavigate();

  const load = async (q = search) => {
    setLoading(true);
    try {
      const data = await api.get(`/superadmin/hotels?search=${q}`);
      setHotels(data.hotels);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    clearTimeout(window._searchTimer);
    window._searchTimer = setTimeout(() => load(e.target.value), 400);
  };

  const toggleActive = async (hotel) => {
    try {
      await api.put(`/superadmin/hotels/${hotel.id}`, { isActive: !hotel.isActive });
      setHotels(hotels.map(h => h.id === hotel.id ? { ...h, isActive: !h.isActive } : h));
      toast.success(hotel.isActive ? 'Otel pasif yapıldı' : 'Otel aktif edildi');
    } catch { toast.error('Güncellenemedi'); }
  };

  const createHotel = async () => {
    if (!newHotel.hotelName || !newHotel.adminEmail || !newHotel.adminPassword) {
      return toast.error('Tüm alanları doldurun');
    }
    try {
      await api.post('/superadmin/hotels', newHotel);
      toast.success('Otel oluşturuldu');
      setShowNew(false);
      setNewHotel({ hotelName: '', adminName: '', adminEmail: '', adminPassword: '', plan: 'STARTER' });
      load();
    } catch { toast.error('Oluşturulamadı'); }
  };

  const planColor = { STARTER: '#94a3b8', PROFESSIONAL: '#3b82f6', ENTERPRISE: '#8b5cf6' };

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Oteller</h1>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{total} otel kayıtlı</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}><Plus size={15} /> Yeni Otel</button>
      </div>

      {/* Arama */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        <input value={search} onChange={handleSearch} placeholder="Otel adı, email veya şehir ara..."
          style={{ paddingLeft: 36, width: '100%', maxWidth: 400 }} />
      </div>

      {/* Yeni Otel Formu */}
      {showNew && (
        <div className="card" style={{ marginBottom: 20, border: '2px solid #3b82f6' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Yeni Otel Oluştur</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label>Otel Adı *</label><input value={newHotel.hotelName} onChange={e => setNewHotel({ ...newHotel, hotelName: e.target.value })} placeholder="Deniz Butik Otel" /></div>
            <div><label>Plan</label>
              <select value={newHotel.plan} onChange={e => setNewHotel({ ...newHotel, plan: e.target.value })}>
                <option value="STARTER">Starter</option>
                <option value="PROFESSIONAL">Professional</option>
                <option value="ENTERPRISE">Enterprise</option>
              </select>
            </div>
            <div><label>Admin Adı</label><input value={newHotel.adminName} onChange={e => setNewHotel({ ...newHotel, adminName: e.target.value })} placeholder="Ahmet Yılmaz" /></div>
            <div><label>Admin Email *</label><input value={newHotel.adminEmail} onChange={e => setNewHotel({ ...newHotel, adminEmail: e.target.value })} placeholder="admin@otel.com" /></div>
            <div><label>Admin Şifre *</label><input type="password" value={newHotel.adminPassword} onChange={e => setNewHotel({ ...newHotel, adminPassword: e.target.value })} placeholder="En az 6 karakter" /></div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn btn-primary" onClick={createHotel}>Oluştur</button>
            <button className="btn btn-secondary" onClick={() => setShowNew(false)}>İptal</button>
          </div>
        </div>
      )}

      {/* Otel Listesi */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Yükleniyor...</div>
        ) : hotels.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Otel bulunamadı</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                {['Otel', 'Plan', 'Konuşmalar', 'Rezervasyonlar', 'Kullanıcılar', 'Durum', ''].map(h => (
                  <th key={h} style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#64748b', textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hotels.map(hotel => (
                <tr key={hotel.id} style={{ borderBottom: '1px solid #f1f5f9' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{hotel.name}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{hotel.city || hotel.email || hotel.slug}</div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: (planColor[hotel.plan] || '#94a3b8') + '18', color: planColor[hotel.plan] || '#94a3b8' }}>
                      {hotel.plan}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: '#475569' }}>{hotel._count.conversations}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: '#475569' }}>{hotel._count.reservations}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: '#475569' }}>{hotel._count.users}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <button onClick={() => toggleActive(hotel)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      {hotel.isActive
                        ? <><CheckCircle size={16} color="#22c55e" /> <span style={{ color: '#22c55e' }}>Aktif</span></>
                        : <><XCircle size={16} color="#ef4444" /> <span style={{ color: '#ef4444' }}>Pasif</span></>}
                    </button>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button onClick={() => navigate(`/superadmin/hotels/${hotel.id}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', display: 'flex', alignItems: 'center' }}>
                      <ChevronRight size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
